import { useState } from 'react';
import { getShopDomain, isEmbeddedApp } from '../utils/api';
import './SettingsPage.css';

const SUPPORTED_LANGUAGES = [
    { locale: 'en-US', flag: '🇺🇸', name: 'English (US)', units: 'imperial' },
    { locale: 'en-GB', flag: '🇬🇧', name: 'English (UK)', units: 'imperial' },
    { locale: 'fr-FR', flag: '🇫🇷', name: 'Français', units: 'metric' },
    { locale: 'de-DE', flag: '🇩🇪', name: 'Deutsch', units: 'metric' },
    { locale: 'es-ES', flag: '🇪🇸', name: 'Español', units: 'metric' },
    { locale: 'pt-PT', flag: '🇵🇹', name: 'Português', units: 'metric' },
    { locale: 'it-IT', flag: '🇮🇹', name: 'Italiano', units: 'metric' },
    { locale: 'nl-NL', flag: '🇳🇱', name: 'Nederlands', units: 'metric' },
    { locale: 'ja-JP', flag: '🇯🇵', name: '日本語', units: 'metric' },
    { locale: 'ar-SA', flag: '🇸🇦', name: 'العربية', units: 'metric' },
];

function SettingsPage() {
    const embedded = isEmbeddedApp();
    const connectedShop = getShopDomain();
    const [shopifyConnected] = useState(() => !!connectedShop);
    const [targetLanguages, setTargetLanguages] = useState<string[]>(() => {
        try {
            const saved = localStorage.getItem('commerium_target_languages');
            return saved ? JSON.parse(saved) : ['en-US'];
        } catch { return ['en-US']; }
    });

    const toggleLanguage = (locale: string) => {
        setTargetLanguages(prev => {
            const updated = prev.includes(locale)
                ? prev.filter(l => l !== locale)
                : [...prev, locale];
            localStorage.setItem('commerium_target_languages', JSON.stringify(updated));
            return updated;
        });
    };

    return (
        <div className="settings-page animate-fade-in">
            <h1 className="page-title">
                <span className="title-icon">⚙️</span>
                Settings
            </h1>
            <p className="page-subtitle">
                Configure your stores and preferences
            </p>

            <section className="settings-section">
                <h2>Connected Stores</h2>

                <div className="store-connections">
                    <div className="store-card">
                        <div className="store-info">
                            <span className="store-icon">🛒</span>
                            <div>
                                <h3>Shopify</h3>
                                <p>
                                    {shopifyConnected
                                        ? `Connected: ${connectedShop}`
                                        : embedded
                                            ? 'Open this app from Shopify Admin to connect your store.'
                                            : 'Install Commerium from the Shopify App Store to connect your store.'}
                                </p>
                            </div>
                        </div>
                        {shopifyConnected ? (
                            <span className="coming-soon-badge" style={{ background: '#dcfce7', color: '#166534' }}>Connected</span>
                        ) : (
                            <a
                                className="btn btn-primary"
                                href="https://apps.shopify.com"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Get on Shopify
                            </a>
                        )}
                    </div>

                    <div className="store-card disabled">
                        <div className="store-info">
                            <span className="store-icon">🎨</span>
                            <div>
                                <h3>Etsy</h3>
                                <p>Coming soon</p>
                            </div>
                        </div>
                        <span className="coming-soon-badge">Soon</span>
                    </div>

                    <div className="store-card disabled">
                        <div className="store-info">
                            <span className="store-icon">📦</span>
                            <div>
                                <h3>Amazon Handmade</h3>
                                <p>Coming soon</p>
                            </div>
                        </div>
                        <span className="coming-soon-badge">Soon</span>
                    </div>

                    <div className="store-card disabled">
                        <div className="store-info">
                            <span className="store-icon">📌</span>
                            <div>
                                <h3>Pinterest</h3>
                                <p>Coming soon</p>
                            </div>
                        </div>
                        <span className="coming-soon-badge">Soon</span>
                    </div>
                </div>
            </section>

            <section className="settings-section">
                <h2>Default Settings</h2>

                <div className="settings-form">
                    <div className="form-group">
                        <label>Default Currency</label>
                        <select defaultValue="EUR">
                            <option value="EUR">Euro (EUR)</option>
                            <option value="USD">US Dollar (USD)</option>
                            <option value="GBP">British Pound (GBP)</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Default Weight Unit</label>
                        <select defaultValue="kg">
                            <option value="kg">Kilograms (kg)</option>
                            <option value="lb">Pounds (lb)</option>
                            <option value="g">Grams (g)</option>
                            <option value="oz">Ounces (oz)</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Default Dimension Unit</label>
                        <select defaultValue="cm">
                            <option value="cm">Centimeters (cm)</option>
                            <option value="in">Inches (in)</option>
                            <option value="mm">Millimeters (mm)</option>
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Default Language</label>
                        <select defaultValue="en">
                            <option value="en">English</option>
                            <option value="es">Español</option>
                            <option value="fr">Français</option>
                            <option value="de">Deutsch</option>
                            <option value="pt">Português</option>
                        </select>
                    </div>
                </div>
            </section>

            {/* Target Markets */}
            <section className="settings-section">
                <div className="section-header-with-badge">
                    <h2>🌍 Target Markets</h2>
                    <span className="feature-badge">Motion Images</span>
                </div>
                <p className="section-desc">
                    Select the countries you sell in. Motion image posts will be generated in each language,
                    with measurements automatically converted per region (metric / imperial).
                </p>
                <div className="language-markets-grid">
                    {SUPPORTED_LANGUAGES.map(lang => (
                        <button
                            key={lang.locale}
                            className={`language-chip ${targetLanguages.includes(lang.locale) ? 'selected' : ''}`}
                            onClick={() => toggleLanguage(lang.locale)}
                            title={lang.units === 'imperial' ? 'Uses imperial units (in, lb)' : 'Uses metric units (cm, kg)'}
                        >
                            <span className="language-chip-flag">{lang.flag}</span>
                            <span className="language-chip-name">{lang.name}</span>
                            {lang.units === 'imperial' && <span className="units-badge">in/lb</span>}
                        </button>
                    ))}
                </div>
                {targetLanguages.length > 0 && (
                    <p className="languages-selected-note">
                        ✓ {targetLanguages.length} market{targetLanguages.length > 1 ? 's' : ''} selected —
                        motion posts will be created for each
                    </p>
                )}
            </section>

            <section className="settings-section">
                <h2>AI Settings</h2>

                <div className="settings-form">
                    <div className="toggle-setting">
                        <div className="toggle-info">
                            <h4>Auto-generate Studio Images</h4>
                            <p>Automatically create professional product photos</p>
                        </div>
                        <label className="toggle-switch">
                            <input type="checkbox" defaultChecked />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>

                    <div className="toggle-setting">
                        <div className="toggle-info">
                            <h4>Multi-marketplace Optimization</h4>
                            <p>Generate descriptions optimized for each platform</p>
                        </div>
                        <label className="toggle-switch">
                            <input type="checkbox" defaultChecked />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>

                    <div className="toggle-setting">
                        <div className="toggle-info">
                            <h4>Auto-translate Listings</h4>
                            <p>Automatically translate listings for international markets</p>
                        </div>
                        <label className="toggle-switch">
                            <input type="checkbox" />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>
                </div>
            </section>

            <section className="settings-section danger-zone">
                <h2>Danger Zone</h2>
                <div className="danger-actions">
                    <button className="btn btn-secondary">Export All Data</button>
                    <button className="btn danger">Delete Account</button>
                </div>
            </section>
        </div>
    );
}

export default SettingsPage;
